import type { ApiErrorBody } from '@kominiarz/shared';
import { markOffline, markOnline } from './appStatus';
import { offlineAnswer } from './offline';

/** Error with a message that can be shown to the user as is (Polish, no codes). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

export const NETWORK_ERROR_MESSAGE = 'Nie udało się połączyć z serwerem. Sprawdź połączenie z internetem.';
export const OFFLINE_WRITE_MESSAGE = 'Brak połączenia z internetem. Zmiany zapiszesz, gdy wróci zasięg.';

/** A gateway answering instead of the app (app restarting behind the proxy) counts as offline. */
const isGatewayError = (res: Response) =>
  [502, 503, 504].includes(res.status) && !res.headers.get('content-type')?.includes('application/json');

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function api<T>(path: string, options: { method?: Method; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  // Known to be offline: do not even try to save.
  if (method !== 'GET' && typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new ApiError(0, 'NETWORK', OFFLINE_WRITE_MESSAGE);
  }

  let res: Response | null = null;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: 'same-origin',
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
  }
  if (!res || isGatewayError(res)) {
    markOffline();
    // Reading without a connection: answer from the device's offline copy if possible.
    if (method === 'GET') {
      const answer = offlineAnswer(path);
      if (answer !== undefined) return answer as T;
    }
    throw new ApiError(0, 'NETWORK', method === 'GET' ? NETWORK_ERROR_MESSAGE : OFFLINE_WRITE_MESSAGE);
  }
  markOnline();

  const data: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const error = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      res.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Coś poszło nie tak. Spróbuj ponownie.',
      error?.fields,
    );
  }
  return data as T;
}

/** multipart/form-data upload (photos); the browser sets the boundary header. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: 'POST', headers, body: form, credentials: 'same-origin' });
  } catch {
    markOffline();
    throw new ApiError(0, 'NETWORK', OFFLINE_WRITE_MESSAGE);
  }
  markOnline();
  const data: unknown = await res.json().catch(() => null);
  // 400 with a body listing rejected files is a normal answer, not an exception.
  if (!res.ok && !(res.status === 400 && data && typeof data === 'object' && 'rejected' in data)) {
    const error = (data as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, error?.code ?? 'UNKNOWN', error?.message ?? 'Nie udało się wysłać zdjęć.', error?.fields);
  }
  return data as T;
}

/** Message for any thrown value, suitable for an alert or toast. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Coś poszło nie tak. Spróbuj ponownie.';
}

export function fieldErrors(err: unknown): Record<string, string> {
  return err instanceof ApiError ? err.fields : {};
}
